import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';

import { Amount } from '@/components/common/Amount';
import { SvgCreditCard, type IconProps } from '@/components/icons';

type AccountItemProps = {
  name: string;
  to: string;
  balance?: number;
  connected?: boolean;
  failed?: boolean;
  bold?: boolean;
  marginTop?: number;
  titleRow?: boolean;
};

/**
 * Port of `sidebar/Account.tsx` (minus drag & drop, which is out of scope):
 * 13px label with a 4px left accent, balance right-aligned in tabular figures.
 */
export function AccountItem({
  name,
  to,
  balance,
  connected,
  failed,
  bold,
  marginTop,
  titleRow,
}: AccountItemProps) {
  const Icon: ComponentType<IconProps> | null = connected ? SvgCreditCard : null;

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        clsx(
          'relative flex items-center gap-1.5 pr-[15px] pl-2.5 no-underline',
          'text-sm text-sidebar-item-text hover:bg-sidebar-item-background-hover',
          'border-l-4 border-l-transparent',
          bold && 'font-semibold',
          titleRow && 'text-sidebar-header-text',
          isActive &&
            'border-l-sidebar-item-accent-selected text-sidebar-item-text-selected',
          failed && 'text-sidebar-text-failed',
        )
      }
      style={{ marginTop }}
      data-testid={`sidebar-account-${name}`}
    >
      {Icon && (
        <Icon
          width={12}
          height={12}
          className="shrink-0 text-sidebar-text-muted"
        />
      )}
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {balance !== undefined && (
        <Amount
          value={balance}
          className="shrink-0 text-sidebar-item-text"
          greyZero={false}
        />
      )}
    </NavLink>
  );
}
